import { useQuery } from '@tanstack/react-query';
import { getHybridRates } from '../services/rates/hybrid-rate-service';
import type { ExchangeRate } from '../services/rates/types';
import { useOfflineSupport } from './useOfflineSupport';

export function useExchangeRates() {
  const { isOnline, storeDataForOffline, getOfflineData } = useOfflineSupport();

  return useQuery<ExchangeRate[], Error>({
    queryKey: ['exchangeRates'],
    queryFn: async () => {
      if (!isOnline) {
        const offlineData = getOfflineData();
        if (offlineData) {
          return offlineData;
        }
        throw new Error('No offline data available');
      }

      const rates = (await getHybridRates()).parallel;
      await storeDataForOffline(rates);
      return rates;
    },
    refetchInterval: 60000,
    staleTime: 45000, // 45 seconds
    gcTime: 600000, // 10 minutes
    retry: 3,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    networkMode: 'online',
    refetchIntervalInBackground: false,
  });
}